import { useEffect } from 'react';

import { useUser } from '@clerk/react-router';

import { usePostHog as usePostHogReact } from 'posthog-js/react';

/**
 * Custom hook for PostHog analytics tracking
 * Provides type-safe event tracking with user context
 */
export function usePostHog() {
  const { user } = useUser();
  const posthog = usePostHogReact();

  // Initialize user identification when user is loaded
  useEffect(() => {
    if (user && posthog) {
      posthog.identify(user.id, {
        email: user.primaryEmailAddress?.emailAddress,
        name: user.fullName,
        created_at: user.createdAt,
      });
    }
  }, [user, posthog]);

  const track = (event: string, properties?: Record<string, any>) => {
    if (posthog) {
      // Get device type for all events
      const deviceType = typeof window !== 'undefined' 
        ? window.innerWidth < 768 
          ? 'mobile' 
          : window.innerWidth < 1024 
            ? 'tablet' 
            : 'desktop'
        : 'unknown';

      posthog.capture(event, {
        ...properties,
        user_id: user?.id,
        user_email: user?.primaryEmailAddress?.emailAddress,
        device_type: deviceType,
        timestamp: new Date().toISOString(),
      });
    }
  };

  const trackPageView = (
    pageName: string,
    properties?: Record<string, any>
  ) => {
    if (!posthog) return;

    // Get device and screen information for better mobile tracking
    const deviceInfo = typeof window !== 'undefined' ? {
      screen_width: window.innerWidth,
      screen_height: window.innerHeight,
      is_mobile: window.innerWidth < 768,
      is_tablet: window.innerWidth >= 768 && window.innerWidth < 1024,
      is_desktop: window.innerWidth >= 1024,
      user_agent: navigator.userAgent,
      referrer: document.referrer,
    } : {};

    // Clean up page name for better readability
    const cleanPageName = pageName
      .replace(/^\//, '') // Remove leading slash
      .replace(/\//g, ' > ') // Replace slashes with breadcrumb style
      .replace(/-/g, ' ') // Replace hyphens with spaces
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize each word
      .join(' ') || 'Home';

    track('page_viewed', {
      page_name: cleanPageName,
      page_path: pageName,
      ...deviceInfo,
      ...properties,
    });
  };

  const trackUserAction = (
    action: string,
    properties?: Record<string, any>
  ) => {
    track('user_action', {
      action,
      ...properties,
    });
  };

  const trackFeatureUsed = (
    feature: string,
    properties?: Record<string, any>
  ) => {
    track('feature_used', {
      feature,
      ...properties,
    });
  };

  const trackError = (error: string, properties?: Record<string, any>) => {
    track('error_occurred', {
      error,
      ...properties,
    });
  };

  const trackConversion = (type: string, properties?: Record<string, any>) => {
    track('conversion', {
      conversion_type: type,
      ...properties,
    });
  };

  return {
    track,
    trackPageView,
    trackUserAction,
    trackFeatureUsed,
    trackError,
    trackConversion,
    isLoaded: !!posthog,
  };
}

// Common event types for consistency
export const EVENTS = {
  // User lifecycle
  USER_SIGNED_UP: 'user_signed_up',
  USER_SIGNED_IN: 'user_signed_in',
  USER_SIGNED_OUT: 'user_signed_out',

  // Practice sessions
  PRACTICE_STARTED: 'practice_started',
  PRACTICE_COMPLETED: 'practice_completed',
  PRACTICE_ABANDONED: 'practice_abandoned',
  VIDEO_RECORDED: 'video_recorded',
  VIDEO_UPLOADED: 'video_uploaded',

  // Analysis
  ANALYSIS_STARTED: 'analysis_started',
  ANALYSIS_COMPLETED: 'analysis_completed',
  ANALYSIS_VIEWED: 'analysis_viewed',
  FEEDBACK_VIEWED: 'feedback_viewed',

  // Subscription
  SUBSCRIPTION_MODAL_OPENED: 'subscription_modal_opened',
  SUBSCRIPTION_STARTED: 'subscription_started',
  SUBSCRIPTION_COMPLETED: 'subscription_completed',
  SUBSCRIPTION_CANCELLED: 'subscription_cancelled',
  SUBSCRIPTION_REACTIVATED: 'subscription_reactivated',
  UPGRADE_CLICKED: 'upgrade_clicked',

  // Navigation
  PAGE_VIEWED: 'page_viewed',
  SIDEBAR_TOGGLED: 'sidebar_toggled',
  NAVIGATION_CLICKED: 'navigation_clicked',
  
  // Mobile specific
  MOBILE_PAGE_VIEWED: 'mobile_page_viewed',
  MOBILE_MENU_OPENED: 'mobile_menu_opened',
  MOBILE_MENU_CLOSED: 'mobile_menu_closed',

  // Features
  WARMUP_STARTED: 'warmup_started',
  LIBRARY_BROWSED: 'library_browsed',
  STATS_VIEWED: 'stats_viewed',
  SETTINGS_ACCESSED: 'settings_accessed',

  // Academy Articles
  ARTICLE_VIEWED: 'article_viewed',
  ARTICLE_SHARED: 'article_shared',
  ARTICLE_LINK_CLICKED: 'article_link_clicked',

  // Quiz
  QUIZ_STARTED: 'quiz_started',
  QUIZ_QUESTION_ANSWERED: 'quiz_question_answered',
  QUIZ_COMPLETED: 'quiz_completed',

  // Errors
  ERROR_OCCURRED: 'error_occurred',
  UPLOAD_FAILED: 'upload_failed',
  ANALYSIS_FAILED: 'analysis_failed',
} as const;
